import { plural } from "@amber/contracts";
import { AppLink, cx, formatBytes, formatCount } from "@amber/ui";
import type { HomeResponse } from "@amber/application/contracts";
import { mediaText, weekly } from "./format";
import { Count, Panel } from "./parts";

const CELL = "px-2 py-1.5 text-right font-mono whitespace-nowrap";

type Platforms = HomeResponse["platforms"];

function PlatformRow({ r, team }: { r: Platforms["rows"][number]; team: boolean }) {
  return (
    <tr className="border-t border-divider">
      <td className="px-4 py-1.5">
        {team ? (
          r.label
        ) : (
          <AppLink href={r.href} className="text-fg no-underline hover:underline">
            {r.label}
          </AppLink>
        )}
      </td>
      <td className={CELL}>{formatCount(r.sources)}</td>
      <td className={CELL}>
        <Count
          href={r.communications.href}
          count={r.communications.count}
          team={team}
          label={`${r.label} communications`}
        />
      </td>
      <td className={cx(CELL, "max-narrow:hidden")}>{formatCount(r.items.count)}</td>
      <td className={cx(CELL, "max-narrow:hidden")}>{mediaText(r.media_seconds, r.pages)}</td>
      <td className={CELL}>
        {formatBytes(r.archive_bytes)}
        {r.deleted_contributing > 0 ? (
          <span className="text-muted"> incl. {formatCount(r.deleted_contributing)} deleted</span>
        ) : null}
      </td>
      <td className={cx(CELL, "pr-4 max-narrow:hidden")}>{weekly(r.weekly_items, r.weekly_bytes)}</td>
    </tr>
  );
}

function TotalRow({ total, team }: { total: Platforms["total"]; team: boolean }) {
  return (
    <tr className="border-t border-divider bg-surface font-semibold">
      <td className="px-4 py-1.5">Total</td>
      <td className={CELL}>
        {formatCount(total.sources)}
        {total.paused > 0 ? (
          <span className="font-normal text-muted"> ({formatCount(total.paused)} paused)</span>
        ) : null}
      </td>
      <td className={CELL}>
        <Count
          href={total.communications.href}
          count={total.communications.count}
          team={team}
          label="All communications"
        />
      </td>
      <td className={cx(CELL, "max-narrow:hidden")}>{formatCount(total.items.count)}</td>
      <td className={cx(CELL, "max-narrow:hidden")}>{mediaText(total.media_seconds, total.pages)}</td>
      <td className={CELL}>
        {formatBytes(total.archive_bytes)}
        {total.deleted_contributing > 0 ? (
          <span className="font-normal text-muted"> incl. {formatCount(total.deleted_contributing)} deleted</span>
        ) : null}
      </td>
      <td className={cx(CELL, "pr-4 max-narrow:hidden")}>{weekly(total.weekly_items, total.weekly_bytes)}</td>
    </tr>
  );
}

export function PlatformsPanel({ data, team }: { data: HomeResponse; team: boolean }) {
  const p = data.platforms;
  return (
    <Panel
      labelledBy="home-platforms"
      title="Collection by platform"
      lead="All time · volume, not coverage"
      link={team ? null : { href: "/sources?tab=platforms", label: "Platforms" }}
    >
      <div className="-mx-4 overflow-x-auto">
        <table className="w-full border-collapse text-md">
          <thead className="bg-surface text-muted">
            <tr>
              <th className="px-4 py-1.5 text-left font-normal">Platform</th>
              <th className="px-2 py-1.5 text-right font-normal">Sources</th>
              <th className="px-2 py-1.5 text-right font-normal">Communications</th>
              <th className="px-2 py-1.5 text-right font-normal max-narrow:hidden">Items</th>
              <th className="px-2 py-1.5 text-right font-normal max-narrow:hidden">Media / pages</th>
              <th className="px-2 py-1.5 text-right font-normal">Archive</th>
              <th className="px-4 py-1.5 text-right font-normal max-narrow:hidden">Per week</th>
            </tr>
          </thead>
          <tbody>
            {p.rows.map((r) => (
              <PlatformRow key={r.key} r={r} team={team} />
            ))}
            {p.not_collected.length > 0 ? (
              <tr className="border-t border-divider text-muted">
                <td className="px-4 py-1.5">Not collected yet</td>
                <td colSpan={6} className="px-2 py-1.5">
                  Known accounts:{" "}
                  {p.not_collected
                    .map((x) => `${x.label} ${plural(x.accounts, "account", { format: formatCount })}`)
                    .join(" · ")}
                </td>
              </tr>
            ) : null}
            <TotalRow total={p.total} team={team} />
          </tbody>
        </table>
      </div>
      <p className="m-0 mt-2 text-sm text-muted">
        Volume, not coverage. Shared files and communications on several platforms count once; totals are all time
        whatever the time frame. Restricted and deleted records are left out of communications and items but stay in
        archive size.
      </p>
    </Panel>
  );
}
